import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import FilterBar from '../components/work/FilterBar';
import ItemContextMenu from '../components/work/ItemContextMenu';
import Peek from '../components/work/Peek';
import QueueInsightCards from '../components/work/QueueInsightCards';
import WorkKanban from '../components/work/WorkKanban';
import WorkLayoutSwitch from '../components/work/WorkLayoutSwitch';
import WorkListPagination from '../components/work/WorkListPagination';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ListEmpty, LoadingRows, PageTitle, WorkPageError } from '../components/work/page-states';
import {
  DueDateCell,
  PriorityLabel,
  ProjectNameCell,
  StateLabel,
  WorkItemTitleLink,
  WorkKeyCell,
} from '../components/work/table-cells';
import { apiFetch, v1Path } from '../lib/api';
import {
  matchesQueueFocus,
  parseQueueFocus,
  startOfDay,
  summarizeAssigned,
  type QueueFocus,
} from '../lib/assigned-queue';
import {
  STATE_GROUPS_ANY,
  clearedWorkListSearchParams,
  filtersForLayoutSwitch,
  filtersToSearchParams,
  searchParamsToFilters,
  type DisplaySpec,
  type FilterSpec,
} from '../lib/filters';
import { opsCreatePath, type OpsItemType } from '../lib/ops-record';
import { fetchStatesByProjectIds } from '../lib/project-states';
import { paginateItems, WORK_LIST_PAGE_SIZE } from '../lib/work-item-list';
import { requireWorkQueue } from '../lib/work-queues';
import {
  STATE_GROUP_BOARD_COLUMNS,
  type ProjectDto,
  type StateDto,
  type WorkItemSlim,
} from '../lib/work-types';

type ListResponse = {
  items: WorkItemSlim[];
  page: { limit: number; offset: number; total: number };
};

type Props = {
  type: OpsItemType;
};

function orderedQueueKeys(
  items: WorkItemSlim[],
  groupByStateId: Map<string, string>,
): string[] {
  const keys: string[] = [];
  for (const column of STATE_GROUP_BOARD_COLUMNS) {
    for (const item of items) {
      if ((groupByStateId.get(item.state_id) ?? item.state_id) === column.id) {
        keys.push(item.key);
      }
    }
  }
  return keys;
}

export default function WorkQueue({ type }: Props) {
  const queue = requireWorkQueue(type);
  const [searchParams, setSearchParams] = useSearchParams();
  const createPath = opsCreatePath(type);
  const [items, setItems] = useState<WorkItemSlim[]>([]);
  const [projectNames, setProjectNames] = useState<Map<string, string>>(new Map());
  const [groupByStateId, setGroupByStateId] = useState<Map<string, string>>(() => new Map());
  const [stateById, setStateById] = useState<Map<string, StateDto>>(() => new Map());
  const [statesByProject, setStatesByProject] = useState<Map<string, StateDto[]>>(() => new Map());
  const [total, setTotal] = useState(0);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(WORK_LIST_PAGE_SIZE);
  const focus = parseQueueFocus(searchParams.get('focus'));
  const today = useMemo(() => startOfDay(new Date()), []);

  const parsed = useMemo(
    () =>
      searchParamsToFilters(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, queue],
  );
  const filters: FilterSpec = { ...parsed.filters, types: [queue.type] };
  const isKanban = parsed.display.layout === 'kanban';
  const display: DisplaySpec = {
    layout: isKanban ? 'kanban' : 'list',
    order_by: parsed.display.order_by ?? '-created_at',
    group_by: isKanban ? 'state' : parsed.display.group_by ?? null,
    show_sub_items: parsed.display.show_sub_items ?? false,
    show_empty_groups: parsed.display.show_empty_groups ?? true,
  };

  const listQueryKey = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('peek');
    return params.toString();
  }, [searchParams]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const params = filtersToSearchParams(filters, display);
      params.set('limit', '100');
      params.set('offset', '0');
      const [list, projectRes, unfiltered] = await Promise.all([
        apiFetch<ListResponse>(`${v1Path('/work-items')}?${params.toString()}`),
        apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
        apiFetch<ListResponse>(
          `${v1Path('/work-items')}?types=${encodeURIComponent(queue.type)}&limit=1&offset=0`,
        ),
      ]);
      const states = await fetchStatesByProjectIds(
        Array.from(new Set(list.items.map((item) => item.project_id))),
      );
      const nextGroups = new Map<string, string>();
      Array.from(states.byId.entries()).forEach(([id, state]) => {
        nextGroups.set(id, state.group);
      });
      setItems(list.items);
      setTotal(list.page.total);
      setQueueEmpty(unfiltered.page.total === 0);
      setProjectNames(new Map(projectRes.items.map((project) => [project.id, project.name])));
      setGroupByStateId(nextGroups);
      setStateById(states.byId);
      setStatesByProject(states.byProject);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to load ${queue.label.toLowerCase()}`);
      setItems([]);
      setTotal(0);
      setQueueEmpty(false);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryKey, queue.label, queue.type]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onChanged() {
      void load({ silent: true });
    }
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  function applyFilters(next: { filters: FilterSpec; display: DisplaySpec }) {
    const peek = searchParams.get('peek') ?? undefined;
    const params = filtersToSearchParams(
      { ...next.filters, types: [queue.type] },
      { ...next.display, layout: isKanban ? 'kanban' : 'list' },
      { peek },
    );
    if (next.filters.state_groups == null) {
      params.set('state_groups', STATE_GROUPS_ANY);
    }
    setSearchParams(params, { replace: true });
  }

  function clearFilters() {
    setSearchParams(
      clearedWorkListSearchParams({
        lockedType: queue.type,
        display: {
          layout: isKanban ? 'kanban' : 'list',
          order_by: '-created_at',
          group_by: isKanban ? 'state' : null,
        },
        peek: searchParams.get('peek'),
      }),
      { replace: true },
    );
  }

  function searchForLayout(next: 'list' | 'kanban'): string {
    const peek = searchParams.get('peek') ?? undefined;
    const nextFilters = filtersForLayoutSwitch(filters, next, queue.defaultStateGroups);
    const params = filtersToSearchParams(
      nextFilters,
      {
        ...display,
        layout: next,
        group_by: next === 'kanban' ? 'state' : null,
      },
      { peek },
    );
    if (nextFilters.state_groups == null) {
      params.set('state_groups', STATE_GROUPS_ANY);
    }
    return params.toString();
  }

  const summary = useMemo(() => summarizeAssigned(items, today), [items, today]);
  const focusedItems = useMemo(
    () => items.filter((item) => matchesQueueFocus(item, focus, today)),
    [focus, items, today],
  );
  const paged = useMemo(
    () => paginateItems(focusedItems, page, pageSize),
    [focusedItems, page, pageSize],
  );
  const peekKeys = isKanban
    ? orderedQueueKeys(focusedItems, groupByStateId)
    : focusedItems.map((item) => item.key);
  const columnIdForItem = useCallback(
    (item: WorkItemSlim) => groupByStateId.get(item.state_id) ?? item.state_id,
    [groupByStateId],
  );

  useEffect(() => {
    setPage(1);
  }, [listQueryKey, focus, pageSize]);

  function toggleFocus(next: QueueFocus) {
    const params = new URLSearchParams(searchParams);
    if (focus === next) params.delete('focus');
    else params.set('focus', next);
    setSearchParams(params, { replace: true });
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
        <PageTitle
          title={queue.label}
          count={loading || error ? undefined : focus ? focusedItems.length : total}
          action={
            <div className="flex items-center gap-2">
              <WorkLayoutSwitch
                isKanban={isKanban}
                listSearch={searchForLayout('list')}
                boardSearch={searchForLayout('kanban')}
              />
              <Button type="button" asChild>
                <Link to={createPath}>
                  <Plus className="h-4 w-4" aria-hidden />
                  {queue.newLabel}
                </Link>
              </Button>
            </div>
          }
        />

        {queue.type === 'incident' || queue.type === 'problem' ? <IssuesTabs current={queue.type} /> : null}

        {loading || error ? null : (
          <QueueInsightCards summary={summary} active={focus} onToggle={toggleFocus} />
        )}

        <FilterBar
          lockedType={queue.type}
          defaultStateGroups={isKanban ? undefined : queue.defaultStateGroups}
          filters={filters}
          display={display}
          onChange={applyFilters}
          onClear={clearFilters}
        />

        <WorkPageError error={error} />

        {error ? null : loading ? (
          <LoadingRows columns={7} />
        ) : queueEmpty ? (
          <BrandEmpty
            title={`No ${queue.label.toLowerCase()}.`}
            description={queue.type === 'incident' ? 'Nothing is broken right now.' : undefined}
          />
        ) : total === 0 ? (
          <ListEmpty
            title="No matches"
            description="Nothing matches the current filters."
            action={
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : isKanban ? (
          <WorkKanban
            columns={[...STATE_GROUP_BOARD_COLUMNS]}
            items={focusedItems}
            showEmptyGroups={display.show_empty_groups !== false}
            canReorder={false}
            columnIdForItem={columnIdForItem}
            onMove={() => undefined}
          />
        ) : focusedItems.length === 0 ? (
          <ListEmpty
            title="No matches"
            description="Nothing matches the current filters."
            action={
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[7.5rem]">Key</TableHead>
                  <TableHead className="min-w-0 w-[32%]">Title</TableHead>
                  <TableHead className="hidden w-[11rem] md:table-cell">Stream</TableHead>
                  <TableHead className="hidden w-28 md:table-cell">Priority</TableHead>
                  <TableHead className="hidden w-32 md:table-cell">Due date</TableHead>
                  <TableHead className="hidden w-[9rem] md:table-cell">Status</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.slice.map((item) => {
                  const state = stateById.get(item.state_id);
                  return (
                    <TableRow key={item.id} id={`issue-${item.id}`} tabIndex={-1}>
                      <TableCell>
                        <WorkKeyCell
                          workItemId={item.id}
                          workItemKey={item.key}
                          projectId={item.project_id}
                        />
                      </TableCell>
                      <TableCell className="min-w-0">
                        <div className="truncate" title={item.title}>
                          <WorkItemTitleLink
                            workItemId={item.id}
                            workItemKey={item.key}
                            projectId={item.project_id}
                            title={item.title}
                          />
                        </div>
                        <span className="mt-0.5 flex items-center gap-2 md:hidden">
                          <PriorityLabel priority={item.priority} />
                          {state ? <StateLabel name={state.name} color={state.color} group={state.group} blocked={Boolean(item.blocked_reason)} /> : null}
                        </span>
                      </TableCell>
                      <TableCell className="hidden min-w-0 md:table-cell">
                        <ProjectNameCell
                          name={projectNames.get(item.project_id)}
                          workItemKey={item.key}
                          projectId={item.project_id}
                        />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <PriorityLabel priority={item.priority} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <DueDateCell value={item.due_date} done={state?.group === 'completed' || state?.group === 'cancelled'} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {state ? <StateLabel name={state.name} color={state.color} group={state.group} blocked={Boolean(item.blocked_reason)} /> : null}
                      </TableCell>
                      <TableCell>
                        <ItemContextMenu
                          projectId={item.project_id}
                          workItemId={item.id}
                          workItemKey={item.key}
                          version={item.version}
                          states={statesByProject.get(item.project_id) ?? []}
                          currentStateId={item.state_id}
                          itemType={item.type}
                          onChanged={() => void load({ silent: true })}
                          onRemoved={(id) => setItems((curr) => curr.filter((row) => row.id !== id))}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label="Row actions"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </ItemContextMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <WorkListPagination
              page={paged.page}
              pages={paged.pages}
              pageSize={pageSize}
              shown={paged.slice.length}
              total={focusedItems.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}

        <Peek itemKeys={peekKeys} />
      </div>
    </PageFrame>
  );
}

function IssuesTabs({ current }: { current: 'incident' | 'problem' }) {
  const tabs = [
    { type: 'incident', label: 'Incidents', to: '/incidents' },
    { type: 'problem', label: 'Problems', to: '/problems' },
  ] as const;
  return (
    <nav aria-label="Issues" className="flex gap-1 border-b">
      {tabs.map((tab) => (
        <Link
          key={tab.type}
          to={tab.to}
          aria-current={tab.type === current ? 'page' : undefined}
          className={
            tab.type === current
              ? '-mb-px border-b-2 border-primary px-3 pb-2 text-sm font-medium text-foreground'
              : '-mb-px border-b-2 border-transparent px-3 pb-2 text-sm text-muted-foreground hover:text-foreground'
          }
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}
